import { Navigate, useLocation } from "react-router-dom";
import { StorageKeys } from "../services/constants/StorageKeys";
import { storage } from "../services/storageService";
import { Routes } from "./RouteConstants";
import { useTenant } from '../contexts/TenantContext';
import useProviderChoice from '../features/provider/hooks/useProviderChoice';

export const ProtectedHomeRoute = ({ children }) => {
  const { homePage } = useTenant();
  if (!homePage) return null; 

  return homePage ? children : <Navigate to={Routes.SUPPLY} />;
};

// On a tenant with several providers the services follow the choice of provider, so the funnel
// starts there (docs/MULTI_PROVIDER.md); a tenant with one always has its choice.
export const ProtectedSupplyRoute = ({ children }) => {
  const { hasChoice } = useProviderChoice();

  return hasChoice ? children : <Navigate to={Routes.PROVIDERS} />;
};

export const ProtectedBookingRoute = ({ children }) => {
  const { hasChoice } = useProviderChoice();
  const selectedSupply = storage.get(StorageKeys.SELECTED_SUPPLY);

  if (!hasChoice) return <Navigate to={Routes.PROVIDERS} />;

  return selectedSupply ? children : <Navigate to={Routes.SUPPLY} />;
};

export const ProtectedConfirmBookingRoute = ({ children }) => {
  const { hasChoice } = useProviderChoice();
  const selectedSupply = storage.get(StorageKeys.SELECTED_SUPPLY);
  const selectedAppointment = storage.get(StorageKeys.SELECTED_APPOINTMENT);
  const isUserAuthenticated = storage.get(StorageKeys.IS_USER_AUTHENTICATED);

  if (!hasChoice) return <Navigate to={Routes.PROVIDERS} />;

  if (!selectedSupply) return <Navigate to={Routes.SUPPLY} />;

  if (!selectedAppointment) return <Navigate to={Routes.BOOKING} />;

  if (!isUserAuthenticated) {
    storage.set(StorageKeys.TARGET_ROUTE_BEFORE_REDIRECT, Routes.CONFIRM_BOOKING);
    return <Navigate to={Routes.LOGIN} />;
  }

  // No pre-reservation is fine: back from signing in the guest has none yet, and the page
  // makes it as it opens (DECISIONS.md D26).
  return children;
};

// The code page is useless without the token of the request it confirms.
export const ProtectedVerifyEmailRoute = ({ children }) => {
  const emailVerification = storage.get(StorageKeys.EMAIL_VERIFICATION);

  return emailVerification?.token ? children : <Navigate to={Routes.LOGIN} />;
};

export const ProtectedProfileRoute = ({ children }) => {
  const isUserAuthenticated = storage.get(StorageKeys.IS_USER_AUTHENTICATED);

  if (!isUserAuthenticated) {
    storage.set(StorageKeys.TARGET_ROUTE_BEFORE_REDIRECT, Routes.PROFILE);
    return <Navigate to={Routes.LOGIN} />;
  }

  return children;
};

// It guards every admin page, so the login returns to the one asked for, not always the calendar.
export const ProtectedDashboardRoute = ({ children }) => {
  const isUserAuthenticated = storage.get(StorageKeys.IS_USER_AUTHENTICATED);
  const { pathname } = useLocation();

  if (!isUserAuthenticated) {
    storage.set(StorageKeys.TARGET_ROUTE_BEFORE_REDIRECT, pathname);
    return <Navigate to={Routes.LOGIN} />;
  }

  return children;
};

